import type { LineClassificationDto } from '@time-tracking/shared';

import { useTasks } from '../../../api/projects.js';

interface TypesPaneProps {
  projectId: string;
  classifications: LineClassificationDto[];
}

/**
 * Read-only view of the org-wide line-classification catalog, scoped with this
 * project's per-type task counts. Creating / editing / deactivating types lives
 * in Administration → Categories & Classifications (their authoritative home).
 */
export function TypesPane({ projectId, classifications }: TypesPaneProps) {
  const tasksQuery = useTasks(projectId);

  const taskCountByClassification = (tasksQuery.data ?? []).reduce<Record<string, number>>(
    (acc, task) => {
      acc[task.classificationId] = (acc[task.classificationId] ?? 0) + 1;
      return acc;
    },
    {},
  );

  return (
    <div className="types-pane">
      <div className="pane-toolbar">
        <span style={{ fontSize: 13, color: 'var(--fg2)' }}>
          The org-wide catalog of task <b>types</b>. Each task gets exactly one. Manage the catalog
          in <b>Administration → Categories &amp; Classifications</b>.
        </span>
      </div>

      <div className="cls-grid" role="list" aria-label="Line classification types">
        {classifications.map((cls) => {
          const taskCount = taskCountByClassification[cls.classificationId] ?? 0;
          return (
            <div
              key={cls.classificationId}
              className={`cls-card ${cls.activeFlag ? 'enabled' : 'disabled'}`}
              role="listitem"
              style={cls.activeFlag ? undefined : { opacity: 0.65 }}
            >
              <div className="top">
                <h4>{cls.name}</h4>
              </div>
              {cls.description && <div className="note">{cls.description}</div>}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--fg3)' }}>
                  {taskCount} tasks on this project
                </span>
                {!cls.activeFlag && (
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: 11,
                      fontWeight: 'var(--fw-semibold)',
                      color: '#8a5800',
                    }}
                  >
                    Inactive
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <aside className="helper-callout" aria-label="How types work">
        <svg
          className="helper-callout__icon"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 3a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm0 3.5c.41 0 .75.34.75.75v3a.75.75 0 0 1-1.5 0v-3c0-.41.34-.75.75-.75z" />
        </svg>
        <div>
          <b>How types work.</b> Every task carries one type, defaulting to <i>Direct</i>. Approvers
          can change a task&apos;s type from the Tasks tab. The catalog is shared across the whole
          org so reports roll up consistently.
        </div>
      </aside>
    </div>
  );
}
